import SectionHeader from "../ui/SectionHeader";
import { fmt } from "../../formatters";
import { FONT, COLORS } from "../../styles";

export default function RiskTab({ analysis, transactions }) {
  const { currentBalance, dailyFactorTotal, activeLenders, monthly, settings } = analysis;
  const loanObligationPct = (monthly.loanPayments / (monthly.totalCredits || 1)) * 100;
  const balanceCoverage = currentBalance / (dailyFactorTotal || 1);
  const cfg = settings || {};
  const coverageCritical = cfg.coverage_critical_days || 3;
  const stackingPct = cfg.stacking_warning_pct || 35;
  const stackingHigh = cfg.stacking_high_positions || 3;
  const lowBalWatch = cfg.low_balance_watch || 10000;

  const score = Math.min(100, Math.round(
    (balanceCoverage < coverageCritical ? 40 : balanceCoverage < 7 ? 20 : 0) +
    (loanObligationPct > stackingPct ? 30 : loanObligationPct > 20 ? 15 : 0) +
    (activeLenders.length >= stackingHigh ? 30 : activeLenders.length >= 2 ? 15 : 0)
  ));
  const risk = score >= 60 ? "HIGH" : score >= 30 ? "MODERATE" : "LOW";
  const riskColor = score >= 60 ? COLORS.red : score >= 30 ? COLORS.orange : COLORS.green;

  const lowBalanceEvents = transactions.filter((t) => { const d = new Date(); d.setDate(d.getDate() - 30); return t.date >= d.toISOString().split("T")[0] && t.balance < lowBalWatch; });

  return (
    <div>
      <SectionHeader title="ACH Bounce Risk Assessment" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 20, fontFamily: FONT }}>Balance vs. Daily Obligations</div>
          {[
            { label: "Current Balance", val: currentBalance, max: 50000, color: currentBalance < (cfg.balance_critical || 5000) ? COLORS.red : COLORS.green },
            { label: "Daily Factor Obligation", val: dailyFactorTotal, max: 5000, color: COLORS.red },
            { label: "Days of Coverage", val: balanceCoverage, max: 30, unit: " days", color: balanceCoverage < coverageCritical ? COLORS.red : COLORS.orange },
          ].map(({ label, val, max, color, unit }) => (
            <div key={label} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: COLORS.muted, fontFamily: FONT }}>{label}</span>
                <span style={{ fontSize: 14, color, fontWeight: 700, fontFamily: FONT }}>{unit ? val.toFixed(1) + unit : fmt(val)}</span>
              </div>
              <div style={{ height: 6, background: COLORS.surface, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min((val / max) * 100, 100)}%`, background: color, borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 20, fontFamily: FONT }}>Risk Score</div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 72, fontWeight: 700, color: riskColor, lineHeight: 1, fontFamily: FONT }}>{score}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: riskColor, marginTop: 8, marginBottom: 24, fontFamily: FONT }}>{risk} RISK</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
              {[
                ["Balance Coverage", balanceCoverage < coverageCritical ? "FAIL" : "PASS", balanceCoverage < coverageCritical ? COLORS.red : COLORS.green],
                ["Obligation Ratio", loanObligationPct > stackingPct ? "FAIL" : "PASS", loanObligationPct > stackingPct ? COLORS.red : COLORS.green],
                ["Position Stacking", activeLenders.length >= stackingHigh ? "FAIL" : "PASS", activeLenders.length >= stackingHigh ? COLORS.red : COLORS.green],
              ].map(([label, result, color]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  <span style={{ fontSize: 13, color: COLORS.muted, fontFamily: FONT }}>{label}</span>
                  <span style={{ fontSize: 12, color, fontWeight: 700, fontFamily: FONT }}>{result}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SectionHeader title="Low Balance Events (Last 30 Days)" />
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        {lowBalanceEvents.slice(0, 15).map((t, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 1fr 140px 100px", padding: "10px 18px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
            <span style={{ fontSize: 12, color: COLORS.dim, fontFamily: FONT }}>{t.date}</span>
            <span style={{ fontSize: 12, color: COLORS.text, fontFamily: FONT }}>{t.name}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: t.balance < (cfg.balance_critical || 5000) ? COLORS.red : COLORS.orange, fontFamily: FONT }}>{fmt(t.balance)}</span>
            <span style={{ fontSize: 10, padding: "3px 8px", background: t.balance < (cfg.balance_critical || 5000) ? COLORS.redLight : COLORS.orangeLight, color: t.balance < (cfg.balance_critical || 5000) ? COLORS.red : "#92400E", borderRadius: 4, height: "fit-content", fontWeight: 600 }}>{t.balance < (cfg.balance_critical || 5000) ? "CRITICAL" : "WATCH"}</span>
          </div>
        ))}
        {lowBalanceEvents.length === 0 && <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: COLORS.muted }}>No low balance events in the last 30 days</div>}
      </div>
    </div>
  );
}
